// src/services/utilityService.js
import { supabase } from '../config';

const UtilityService = {
  // ===========================================
  // Authentication Utilities
  // ===========================================
  getAuthHeaders: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const headers = {
        'Content-Type': 'application/json',
      };
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }
      return headers;
    } catch (error) {
      console.error('Error getting auth headers:', error);
      return {
        'Content-Type': 'application/json',
      };
    }
  },

  // ===========================================
  // Response Handling
  // ===========================================
  handleResponse: async (response) => {
    if (!response.ok) {
      try {
        const errorData = await response.json();
        const errorMessage = errorData.error || errorData.message || `HTTP error! status: ${response.status}`;
        const error = new Error(errorMessage);
        error.status = response.status;
        error.data = errorData;
        throw error;
      } catch (e) {
        if (e.status) {
          throw e; // Already formatted error
        }
        // If JSON parsing failed, try to get text
        try {
          const text = await response.text();
          const error = new Error(text || `HTTP error! status: ${response.status}`);
          error.status = response.status;
          throw error;
        } catch (textError) {
          const error = new Error(`HTTP error! status: ${response.status}`);
          error.status = response.status;
          throw error;
        }
      }
    }
    
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }
    return response.text();
  },

  // ===========================================
  // API Request Helper
  // ===========================================
  makeRequest: async (url, options = {}) => {
    try {
      const headers = await UtilityService.getAuthHeaders();
      
      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...options.headers
        }
      });

      return UtilityService.handleResponse(response);
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  },

  // ===========================================
  // Data Validation Utilities
  // ===========================================
  validateEmail: (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  validateRequired: (value, fieldName) => {
    if (!value || (typeof value === 'string' && !value.trim())) {
      throw new Error(`${fieldName} is required`);
    }
    return true;
  },

  // ===========================================
  // Data Formatting Utilities
  // ===========================================
  formatDate: (date) => {
    if (!date) return '';
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return '';
      
      const day = dateObj.getDate();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[dateObj.getMonth()];
      const year = dateObj.getFullYear();
      
      return `${day} ${month} ${year}`;
    } catch {
      return '';
    }
  },

  formatDateTime: (date) => {
    if (!date) return '';
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return '';
      
      const day = dateObj.getDate();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[dateObj.getMonth()];
      const year = dateObj.getFullYear();
      const hours = dateObj.getHours().toString().padStart(2, '0');
      const minutes = dateObj.getMinutes().toString().padStart(2, '0');
      
      return `${day} ${month} ${year}, ${hours}:${minutes}`;
    } catch {
      return '';
    }
  },

  formatCurrency: (amount, currency = 'USD') => {
    if (amount === null || amount === undefined) return '';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
      }).format(amount);
    } catch {
      return `${amount}`;
    }
  },

  // ===========================================
  // Error Handling Utilities
  // ===========================================
  handleError: (error, defaultMessage = 'An error occurred') => {
    console.error('Error:', error);
    
    if (error.message) {
      return error.message;
    }
    
    if (typeof error === 'string') {
      return error;
    }
    
    return defaultMessage;
  },

  // ===========================================
  // Local Storage Utilities (for non-sensitive data)
  // ===========================================
  setLocalStorage: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error('Error setting localStorage:', error);
      return false;
    }
  },

  getLocalStorage: (key, defaultValue = null) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.error('Error getting localStorage:', error);
      return defaultValue;
    }
  },

  removeLocalStorage: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Error removing localStorage:', error);
      return false;
    }
  },

  // ===========================================
  // Array and Object Utilities
  // ===========================================
  groupBy: (array, key) => {
    return array.reduce((result, item) => {
      const group = item[key];
      if (!result[group]) {
        result[group] = [];
      }
      result[group].push(item);
      return result;
    }, {});
  },

  sortBy: (array, key, direction = 'asc') => {
    return [...array].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];
      
      if (direction === 'desc') {
        return bVal > aVal ? 1 : -1;
      }
      return aVal > bVal ? 1 : -1;
    });
  },

  // ===========================================
  // Debouncing Utility
  // ===========================================
  debounce: (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },

  // ===========================================
  // File Utilities
  // ===========================================
  getFileExtension: (filename) => {
    return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2);
  },

  formatFileSize: (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  // ===========================================
  // URL Utilities
  // ===========================================
  buildUrl: (base, params = {}) => {
    const url = new URL(base, window.location.origin);
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined) {
        url.searchParams.append(key, params[key]);
      }
    });
    return url.toString();
  },

  // ===========================================
  // UUID Generator (simple)
  // ===========================================
  generateId: () => {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  },

  // ===========================================
  // User Display Name Utilities
  // ===========================================
  /**
   * Get display name from user profile or user object
   * Priority: first_name + last_name > first_name > last_name > username > email prefix > fallback
   * @param {Object} user - User object with first_name, last_name, username, email, or full_name (deprecated)
   * @param {string} fallback - Fallback text if no name found (default: 'N/A')
   * @returns {string} Display name
   */
  getUserDisplayName: (user, fallback = 'N/A') => {
    if (!user) return fallback;
    
    // Use first_name + last_name if both available
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`.trim();
    }
    
    // Use individual parts if available
    if (user.first_name) return user.first_name;
    if (user.last_name) return user.last_name;
    
    // Fallback to username
    if (user.username) return user.username;
    
    // Fallback to email prefix
    if (user.email) {
      return user.email.split('@')[0];
    }
    
    // Legacy support: full_name (deprecated, will be removed)
    if (user.full_name) return user.full_name;
    
    return fallback;
  },

  /**
   * Get user initials from display name
   * @param {Object} user - User object
   * @returns {string} User initials (1-2 characters)
   */
  getUserInitials: (user) => {
    const displayName = UtilityService.getUserDisplayName(user, '');
    if (!displayName) return 'U';
    
    // If name contains spaces, use first and last initial
    if (displayName.includes(' ')) {
      const parts = displayName.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
      }
    }
    
    // Single word name or email prefix - use first character
    return displayName.charAt(0).toUpperCase();
  }
};

export default UtilityService;

// Named exports for common utilities
export const { 
  getAuthHeaders, 
  handleResponse, 
  makeRequest,
  validateEmail,
  validateRequired,
  formatDate,
  formatDateTime,
  formatCurrency,
  handleError,
  debounce,
  getUserDisplayName,
  getUserInitials
} = UtilityService;